import React, { useEffect, useState } from 'react';
import { useTheme } from '../theme';

// Snowflake component with physics
function Snowflake({ id, tokens }) {
  const [pos, setPos] = useState({
    x: Math.random() * 100,
    y: -10,
    drift: (Math.random() - 0.5) * 2,
    speed: 0.3 + Math.random() * 0.7,
    size: 2 + Math.random() * 4,
    opacity: 0.3 + Math.random() * 0.7,
    rotation: Math.random() * 360,
    rotationSpeed: (Math.random() - 0.5) * 2,
  });

  useEffect(() => {
    const interval = setInterval(() => {
      setPos(prev => {
        const newY = prev.y + prev.speed;
        const newX = prev.x + prev.drift * 0.1;
        const newRotation = prev.rotation + prev.rotationSpeed;
        
        if (newY > 110) {
          return {
            x: Math.random() * 100,
            y: -10,
            drift: (Math.random() - 0.5) * 2,
            speed: 0.3 + Math.random() * 0.7,
            size: 2 + Math.random() * 4,
            opacity: 0.3 + Math.random() * 0.7,
            rotation: Math.random() * 360,
            rotationSpeed: (Math.random() - 0.5) * 2,
          };
        }
        
        return { ...prev, y: newY, x: newX, rotation: newRotation };
      });
    }, 50);
    
    return () => clearInterval(interval);
  }, []);

  return (
    <div style={{
      position: 'absolute',
      left: `${pos.x}%`,
      top: `${pos.y}%`,
      width: pos.size,
      height: pos.size,
      background: '#fff',
      borderRadius: '50%',
      opacity: pos.opacity,
      boxShadow: '0 0 4px rgba(255,255,255,0.8)',
      transform: `rotate(${pos.rotation}deg)`,
      pointerEvents: 'none',
      zIndex: 1,
    }} />
  );
}

// Santa Hat decoration for tile corners - elegant Christmas theme
export function SantaHat({ accentColor, size = 50, isFeatured = false }) {
  // Adjust positioning for featured (larger) tiles
  const topPos = isFeatured ? -28 : -25;
  const rightPos = isFeatured ? -28 : -25;
  
  return (
    <svg width={size} height={size} viewBox="0 0 50 50" style={{ position: 'absolute', top: topPos, right: rightPos, zIndex: 1000, transform: 'rotate(45deg)', filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))' }}>
      {/* Main hat body - rich red/accent color with stroke */}
      <path d="M 14 36 Q 14 20, 25 10 Q 36 20, 36 36 Z" fill={accentColor} stroke="#8B0000" strokeWidth="1.5" />
      {/* Highlight on hat */}
      <path d="M 18 32 Q 18 22, 25 14 Q 28 18, 28 28 Z" fill="#fff" opacity="0.25" />
      {/* White fluffy pom-pom on top with stroke */}
      <circle cx="25" cy="10" r="6.5" fill="#fff" stroke="#ddd" strokeWidth="1" />
      <circle cx="25" cy="10" r="5" fill="#f8f8f8" />
      {/* White fluffy trim at bottom - multiple circles for texture with strokes */}
      <ellipse cx="16" cy="35" rx="4" ry="3.5" fill="#fff" stroke="#e0e0e0" strokeWidth="0.8" />
      <ellipse cx="21" cy="36" rx="4.5" ry="4" fill="#fff" stroke="#e0e0e0" strokeWidth="0.8" />
      <ellipse cx="25" cy="37" rx="5" ry="4.5" fill="#fff" stroke="#e0e0e0" strokeWidth="0.8" />
      <ellipse cx="29" cy="36" rx="4.5" ry="4" fill="#fff" stroke="#e0e0e0" strokeWidth="0.8" />
      <ellipse cx="34" cy="35" rx="4" ry="3.5" fill="#fff" stroke="#e0e0e0" strokeWidth="0.8" />
      {/* Inner trim detail */}
      <ellipse cx="25" cy="36" rx="3.5" ry="3" fill="#f0f0f0" />
    </svg>
  );
}

// Pumpkin decoration for Halloween
export function Pumpkin({ size = 28 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" style={{ position: 'absolute', top: -2, right: -2, zIndex: 10 }}>
      <ellipse cx="16" cy="18" rx="12" ry="10" fill="#ff6b00" />
      <path d="M 16 8 Q 18 10, 16 12" stroke="#228B22" strokeWidth="2" fill="none" />
      <path d="M 12 16 L 14 18 L 12 20" fill="#000" />
      <path d="M 20 16 L 18 18 L 20 20" fill="#000" />
      <path d="M 12 22 Q 16 24, 20 22" stroke="#000" strokeWidth="2" fill="none" />
    </svg>
  );
}

// Spider Web decoration for Halloween - top left corner
export function SpiderWeb({ size = 45 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 45 45" style={{ position: 'absolute', top: -2, left: -2, zIndex: 10, filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.3))' }}>
      {/* Web strands */}
      <path d="M 0 0 L 45 45" stroke="#ddd" strokeWidth="1.5" opacity="0.7" />
      <path d="M 0 0 L 45 22" stroke="#ddd" strokeWidth="1.5" opacity="0.7" />
      <path d="M 0 0 L 22 45" stroke="#ddd" strokeWidth="1.5" opacity="0.7" />
      <path d="M 0 0 L 45 0" stroke="#ddd" strokeWidth="1.5" opacity="0.7" />
      <path d="M 0 0 L 0 45" stroke="#ddd" strokeWidth="1.5" opacity="0.7" />
      {/* Concentric curves */}
      <path d="M 8 0 Q 8 8, 0 8" stroke="#ddd" strokeWidth="1.2" fill="none" opacity="0.6" />
      <path d="M 16 0 Q 16 16, 0 16" stroke="#ddd" strokeWidth="1.2" fill="none" opacity="0.6" />
      <path d="M 24 0 Q 24 24, 0 24" stroke="#ddd" strokeWidth="1.2" fill="none" opacity="0.6" />
      {/* Spider */}
      <circle cx="12" cy="12" r="3" fill="#000" />
      <circle cx="12" cy="12" r="1.5" fill="#1a1a1a" />
    </svg>
  );
}

// Snow accumulation on tile bottom - visible white snow layer
export function SnowAccumulation({ isFeatured = false, isDark = false }) {
  // Remove blue shadows on white themes - use subtle gray instead
  const shadowColor = isDark ? 'rgba(100,149,237,0.3)' : 'rgba(180,180,200,0.2)';
  const boxShadowValue = isDark 
    ? '0 -2px 4px rgba(100,149,237,0.4), inset 0 1px 3px rgba(255,255,255,0.9), 0 1px 2px rgba(0,0,0,0.15)'
    : 'inset 0 1px 3px rgba(255,255,255,0.9), 0 1px 2px rgba(0,0,0,0.08)';
  
  return (
    <div style={{
      position: 'absolute',
      bottom: 0,
      left: 0,
      right: 0,
      height: '12px',
      background: 'linear-gradient(to top, rgba(245,250,255,0.98), rgba(245,250,255,0))',
      borderRadius: '0 0 16px 16px',
      pointerEvents: 'none',
      zIndex: 1000,
      boxShadow: boxShadowValue,
      filter: `drop-shadow(0 -1px 2px ${shadowColor})`,
    }}>
      {/* Snow peaks for irregular top edge */}
      <svg width="100%" height="12" style={{ position: 'absolute', top: -6 }}>
        <path d="M 0 6 Q 20 2, 40 6 T 80 6 T 120 6 T 160 6 T 200 6 T 240 6 T 280 6 T 320 6 L 320 12 L 0 12 Z" 
          fill="rgba(245,250,255,1)" 
          stroke={isDark ? "rgba(100,149,237,0.5)" : "rgba(220,220,235,0.6)"} 
          strokeWidth="1" />
      </svg>
    </div>
  );
}

// Easter Egg decoration
export function EasterEgg({ size = 28 }) {
  const colors = ['#ff6b9d', '#c084fc', '#60a5fa', '#4ade80', '#fbbf24'];
  const color = colors[Math.floor(Math.random() * colors.length)];
  
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" style={{ position: 'absolute', top: -2, right: -2, zIndex: 10 }}>
      <ellipse cx="16" cy="17" rx="10" ry="13" fill={color} />
      <path d="M 11 12 Q 16 10, 21 12" stroke="#fff" strokeWidth="2" fill="none" opacity="0.7" />
      <path d="M 11 17 Q 16 15, 21 17" stroke="#fff" strokeWidth="2" fill="none" opacity="0.7" />
      <path d="M 11 22 Q 16 20, 21 22" stroke="#fff" strokeWidth="2" fill="none" opacity="0.7" />
    </svg>
  );
}

// Valentine Heart
export function ValentineHeart({ size = 28 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" style={{ position: 'absolute', top: -2, right: -2, zIndex: 10 }}>
      <path d="M 16 28 C 16 28, 6 20, 6 13 C 6 8, 9 6, 12 6 C 14 6, 16 8, 16 8 C 16 8, 18 6, 20 6 C 23 6, 26 8, 26 13 C 26 20, 16 28, 16 28 Z" fill="#ff1744" />
      <ellipse cx="13" cy="11" rx="3" ry="2" fill="#ff4569" opacity="0.6" />
    </svg>
  );
}

// Bat for Halloween
function FlyingBat({ id }) {
  const [pos, setPos] = useState({
    x: Math.random() * 100,
    y: 10 + Math.random() * 30,
    direction: Math.random() > 0.5 ? 1 : -1,
    speed: 0.3 + Math.random() * 0.5,
    wing: 0,
  });

  useEffect(() => {
    const interval = setInterval(() => {
      setPos(prev => {
        const newX = prev.x + prev.direction * prev.speed;
        const newWing = (prev.wing + 15) % 360;
        
        if (newX > 105 || newX < -5) {
          return {
            x: prev.direction > 0 ? -5 : 105,
            y: 10 + Math.random() * 30,
            direction: prev.direction,
            speed: 0.3 + Math.random() * 0.5,
            wing: newWing,
          };
        }
        
        return { ...prev, x: newX, wing: newWing };
      });
    }, 50);
    
    return () => clearInterval(interval);
  }, []);

  const wingFlap = Math.sin(pos.wing * Math.PI / 180) * 0.3;
  
  return (
    <div style={{
      position: 'absolute',
      left: `${pos.x}%`,
      top: `${pos.y}%`,
      transform: `scaleX(${pos.direction})`,
      pointerEvents: 'none',
      zIndex: 1,
    }}>
      <svg width="32" height="20" viewBox="0 0 32 20">
        <path d={`M 8 10 Q 4 ${10 + wingFlap * 10}, 2 8 L 2 6 Q 4 ${8 - wingFlap * 10}, 8 10`} fill="#1a1a1a" />
        <ellipse cx="16" cy="10" rx="4" ry="3" fill="#1a1a1a" />
        <path d={`M 24 10 Q 28 ${10 + wingFlap * 10}, 30 8 L 30 6 Q 28 ${8 - wingFlap * 10}, 24 10`} fill="#1a1a1a" />
      </svg>
    </div>
  );
}

// Floating hearts for Valentine's
// eslint-disable-next-line no-unused-vars
function FloatingHeart({ id }) {
  const [pos, setPos] = useState({
    x: Math.random() * 100,
    y: 110,
    speed: 0.4 + Math.random() * 0.6,
    drift: (Math.random() - 0.5) * 1.5,
    size: 12 + Math.random() * 16,
    opacity: 0.4 + Math.random() * 0.4,
    rotation: Math.random() * 30 - 15,
  });

  useEffect(() => {
    const interval = setInterval(() => {
      setPos(prev => {
        const newY = prev.y - prev.speed;
        const newX = prev.x + prev.drift * 0.1;
        
        if (newY < -10) {
          return {
            x: Math.random() * 100,
            y: 110,
            speed: 0.4 + Math.random() * 0.6,
            drift: (Math.random() - 0.5) * 1.5,
            size: 12 + Math.random() * 16,
            opacity: 0.4 + Math.random() * 0.4,
            rotation: Math.random() * 30 - 15,
          };
        }
        
        return { ...prev, y: newY, x: newX };
      });
    }, 50);
    
    return () => clearInterval(interval);
  }, []);

  return (
    <div style={{
      position: 'absolute',
      left: `${pos.x}%`,
      top: `${pos.y}%`,
      transform: `rotate(${pos.rotation}deg)`,
      pointerEvents: 'none',
      zIndex: 1,
      opacity: pos.opacity,
    }}>
      <svg width={pos.size} height={pos.size} viewBox="0 0 32 32">
        <path d="M 16 28 C 16 28, 6 20, 6 13 C 6 8, 9 6, 12 6 C 14 6, 16 8, 16 8 C 16 8, 18 6, 20 6 C 23 6, 26 8, 26 13 C 26 20, 16 28, 16 28 Z" fill="#ff1744" />
      </svg>
    </div>
  );
}

// Fireworks particle for New Year
// eslint-disable-next-line no-unused-vars
function Firework({ id }) {
  const [particles, setParticles] = useState([]);
  const [exploded, setExploded] = useState(false);

  useEffect(() => {
    const timeout = setTimeout(() => {
      const x = 20 + Math.random() * 60;
      const y = 20 + Math.random() * 40;
      const color = ['#ff0844', '#ffb700', '#00d9ff', '#00ff88', '#a855f7'][Math.floor(Math.random() * 5)];
      
      const newParticles = Array.from({ length: 20 }, (_, i) => ({
        id: i,
        x,
        y,
        angle: (i / 20) * Math.PI * 2,
        speed: 2 + Math.random() * 3,
        color,
        life: 100,
      }));
      
      setParticles(newParticles);
      setExploded(true);
    }, Math.random() * 2000);

    return () => clearTimeout(timeout);
  }, []);

  useEffect(() => {
    if (!exploded) return;
    
    const interval = setInterval(() => {
      setParticles(prev => {
        const updated = prev.map(p => ({
          ...p,
          x: p.x + Math.cos(p.angle) * p.speed * 0.5,
          y: p.y + Math.sin(p.angle) * p.speed * 0.5 + 0.3,
          speed: p.speed * 0.95,
          life: p.life - 2,
        })).filter(p => p.life > 0);
        
        if (updated.length === 0) {
          setTimeout(() => {
            const x = 20 + Math.random() * 60;
            const y = 20 + Math.random() * 40;
            const color = ['#ff0844', '#ffb700', '#00d9ff', '#00ff88', '#a855f7'][Math.floor(Math.random() * 5)];
            
            const newParticles = Array.from({ length: 20 }, (_, i) => ({
              id: i,
              x,
              y,
              angle: (i / 20) * Math.PI * 2,
              speed: 2 + Math.random() * 3,
              color,
              life: 100,
            }));
            
            setParticles(newParticles);
          }, 1000);
        }
        
        return updated;
      });
    }, 50);
    
    return () => clearInterval(interval);
  }, [exploded]);

  return (
    <>
      {particles.map(p => (
        <div key={p.id} style={{
          position: 'absolute',
          left: `${p.x}%`,
          top: `${p.y}%`,
          width: 4,
          height: 4,
          borderRadius: '50%',
          background: p.color,
          boxShadow: `0 0 8px ${p.color}`,
          opacity: p.life / 100,
          pointerEvents: 'none',
          zIndex: 1,
        }} />
      ))}
    </>
  );
}

// Main festive effects overlay component - ONLY Christmas and Halloween
export default function FestiveEffects({ theme }) {
  const { tokens } = useTheme();
  
  // Only show effects for Christmas and Halloween
  if (theme !== 'christmas' && theme !== 'halloween') return null;

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      pointerEvents: 'none',
      zIndex: 999,
      overflow: 'hidden',
    }}>
      {theme === 'christmas' && (
        <>
          {Array.from({ length: 50 }, (_, i) => <Snowflake key={i} id={i} tokens={tokens} />)}
        </>
      )}
      
      {theme === 'halloween' && (
        <>
          {Array.from({ length: 5 }, (_, i) => <FlyingBat key={i} id={i} />)}
        </>
      )}
    </div>
  );
}

// Export decoration component based on theme - ONLY Christmas and Halloween
export function getTileDecoration(theme, accentColor, themeKey, isFeatured = false, isDark = false) {
  switch (theme) {
    case 'christmas':
      return (
        <>
          <SantaHat accentColor={accentColor} isFeatured={isFeatured} />
          <SnowAccumulation isFeatured={isFeatured} isDark={isDark} />
        </>
      );
    case 'halloween':
      return (
        <>
          <Pumpkin />
          <SpiderWeb />
        </>
      );
    case 'easter':
    case 'valentine':
    case 'newyear':
    default:
      return null;
  }
}
